import { useEffect, useState } from "react";
import { Wordmark } from "@/components/Brand";
import { APK_URL, APP_STORE_URL } from "@/lib/brand";
import { useCountry } from "@/context/CountryContext";
import { Menu, X, Download, ChevronDown, Apple } from "lucide-react";

const LINKS = [
  { label: "Try it yourself", href: "#demo" },
  { label: "Features", href: "#features" },
  { label: "2027 compliance", href: "#compliance" },
  { label: "Pro+ plans", href: "#proplus" },
  { label: "App-only", href: "#pricing" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { country, region, openSelector } = useCountry();

  const CountryPill = ({ testid, onClick }) => country && (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1.5 text-xs font-semibold transition-colors ${
        scrolled ? "border-[#E5E5E5] text-[#2B2B2B] hover:border-[#2BBFB3]" : "border-white/20 text-white hover:border-[#2BBFB3]"
      }`}
      title="Change region"
    >
      <span className="block w-6 h-4 rounded-[3px] overflow-hidden ring-1 ring-black/10"><country.Flag /></span>
      {region ? `${country.code} · ${region.code}` : country.code}
      <ChevronDown size={13} className="opacity-60" />
    </button>
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-[#E5E5E5] py-3" : "bg-white/0 py-5"
      }`}
    >
      <nav className="mx-auto max-w-7xl px-5 sm:px-8 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="shrink-0">
          <Wordmark light={!scrolled} />
        </a>

        <div className="hidden lg:flex items-center gap-5 xl:gap-7">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              className={`text-sm font-medium whitespace-nowrap transition-colors ${
                scrolled
                  ? "text-[#2B2B2B]/70 hover:text-[#2B2B2B]"
                  : "text-white/75 hover:text-white"
              }`}
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-2">
          <CountryPill testid="nav-country-btn" onClick={openSelector} />
          <a
            href={APK_URL}
            data-testid="nav-download-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#2BBFB3] px-4 py-2.5 text-sm font-semibold text-[#0f1211] hover:bg-[#1FA097] transition-colors"
          >
            <Download size={16} /> <span className="hidden xl:inline">Download for&nbsp;</span>Android
          </a>
          <a
            href={APP_STORE_URL}
            data-testid="nav-download-ios-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#2BBFB3] px-4 py-2.5 text-sm font-semibold text-[#0f1211] hover:bg-[#1FA097] transition-colors"
          >
            <Apple size={16} /> <span className="hidden xl:inline">Download for&nbsp;</span>iOS
          </a>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <CountryPill testid="nav-country-btn-mobile" onClick={openSelector} />
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className={`p-2 transition-colors ${scrolled ? "text-[#2B2B2B]" : "text-white"}`}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="lg:hidden mt-3 mx-4 rounded-2xl bg-white border border-[#E5E5E5] p-4 shadow-xl" data-testid="mobile-menu">
          <div className="flex flex-col gap-1">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="px-3 py-2.5 rounded-lg text-sm font-medium text-[#2B2B2B]/80 hover:bg-[#F7F7F7]"
              >
                {l.label}
              </a>
            ))}
            <a
              href={APK_URL}
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#2BBFB3] px-5 py-3 text-sm font-semibold text-[#0f1211]"
            >
              <Download size={16} /> Download for Android
            </a>
            <a
              href={APP_STORE_URL}
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-download-ios-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#2BBFB3] px-5 py-3 text-sm font-semibold text-[#0f1211]"
            >
              <Apple size={16} /> Download for iOS
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
